/**
 * Visual panel for the candidate import state machine (phase 3A).
 * Renders three of the states from docs/designs/candidate-import-progress.svg:
 *  - B: in-flight rail + status + cancel
 *  - C: success toast
 *  - D: error toast
 * Idle state is unchanged from the existing page and handled by the caller.
 */

import React from 'react';
import {
  formatElapsed,
  stepIndexFor,
  type ImportError,
  type ImportState,
  type ImportSuccessSummary,
} from './importState';

interface Props {
  state: ImportState;
  onCancel: () => void;
  onDismiss: () => void;
  onRetry: () => void;
  onOpenPerson?: (personId: number) => void;
  /** Pool import vs profile update — tweaks success copy. */
  context?: 'pool' | 'profile';
}

const STEPS = ['Uploaded', 'Extracted', 'Analyzing', 'Saving'] as const;

function stepStatus(
  index: number,
  active: number,
  isInFlight: boolean
): 'done' | 'active' | 'pending' {
  if (!isInFlight) return 'pending';
  if (index < active) return 'done';
  if (index === active) return 'active';
  return 'pending';
}

export const ImportProgressPanel: React.FC<Props> = ({
  state,
  onCancel,
  onDismiss,
  onRetry,
  onOpenPerson,
  context = 'pool',
}) => {
  const { phase, fileName, elapsedMs, success, error, mode } = state;

  if (phase === 'idle') return null;

  const isInFlight = phase === 'uploading' || phase === 'parsing' || phase === 'saving';
  const active = stepIndexFor(phase);
  const elapsedLabel = formatElapsed(elapsedMs);
  const showStillWorking = elapsedMs > 30_000;

  return (
    <div className="mt-3 space-y-3" data-testid="import-progress-panel">
      {isInFlight && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-xl border border-indigo-700/50 bg-indigo-950/30 p-4"
          data-testid="import-in-flight"
        >
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div>
              <div className="text-sm font-semibold text-white">
                Importing {fileName || (mode === 'paste' ? 'pasted text' : 'CV')}
              </div>
              <div className="text-xs text-dark-text-secondary">
                Parsing uses an LLM and typically takes 10–20 seconds. Do not close this tab.
              </div>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="text-xs px-3 py-1.5 rounded-lg border border-red-800/70 text-red-300 hover:bg-red-900/30"
              data-testid="import-cancel-btn"
            >
              Cancel
            </button>
          </div>

          <ol
            className="flex items-center gap-1 mb-3"
            aria-label="Import progress steps"
          >
            {STEPS.map((label, i) => {
              const status = stepStatus(i, active, isInFlight);
              return (
                <React.Fragment key={label}>
                  <li
                    className="flex flex-col items-center min-w-[68px]"
                    data-testid={`import-step-${label.toLowerCase()}`}
                    data-status={status}
                  >
                    <span
                      className={`flex items-center justify-center w-6 h-6 rounded-full border-2 text-[10px] font-bold ${
                        status === 'done'
                          ? 'bg-green-600 border-green-500 text-white'
                          : status === 'active'
                            ? 'border-indigo-400 text-indigo-300 animate-pulse'
                            : 'border-gray-700 text-gray-500'
                      }`}
                    >
                      {status === 'done' ? '✓' : i + 1}
                    </span>
                    <span
                      className={`mt-1 text-[10px] ${
                        status === 'active'
                          ? 'text-indigo-200 font-semibold'
                          : status === 'done'
                            ? 'text-green-300'
                            : 'text-gray-500'
                      }`}
                    >
                      {label}
                    </span>
                  </li>
                  {i < STEPS.length - 1 && (
                    <span
                      className={`flex-1 h-0.5 rounded ${
                        status === 'done' ? 'bg-green-600' : 'bg-gray-800'
                      }`}
                      aria-hidden="true"
                    />
                  )}
                </React.Fragment>
              );
            })}
          </ol>

          <div className="flex items-center gap-3">
            <span
              className="inline-block w-4 h-4 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin"
              aria-hidden="true"
            />
            <div className="flex-1 text-sm text-dark-text-secondary">
              <span className="text-indigo-300 font-semibold">
                {phase === 'uploading'
                  ? 'Uploading file…'
                  : phase === 'parsing'
                    ? 'Analyzing CV with AI…'
                    : 'Saving to database…'}
              </span>
              <span className="ml-2 text-xs text-gray-500">Elapsed {elapsedLabel}</span>
              {showStillWorking && (
                <span className="ml-2 text-xs text-amber-400">
                  Still working. Azure OpenAI can be slow under load.
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {phase === 'success' && success && (
        <SuccessToast
          summary={success}
          onDismiss={onDismiss}
          onOpenPerson={onOpenPerson}
          context={context}
        />
      )}

      {phase === 'error' && error && (
        <ErrorToast error={error} onDismiss={onDismiss} onRetry={onRetry} />
      )}
    </div>
  );
};

const SuccessToast: React.FC<{
  summary: ImportSuccessSummary;
  onDismiss: () => void;
  onOpenPerson?: (personId: number) => void;
  context: 'pool' | 'profile';
}> = ({ summary, onDismiss, onOpenPerson, context }) => {
  const evidenceBits: string[] = [];
  if (summary.experience_rows) evidenceBits.push(`${summary.experience_rows} roles`);
  if (summary.language_rows) evidenceBits.push(`${summary.language_rows} languages`);
  if (summary.skill_rows)
    evidenceBits.push(`${summary.skill_rows + summary.skill_phrase_rows} skills`);

  const headlineBits: string[] = [];
  if (summary.title) headlineBits.push(summary.title);
  if (summary.primary_location) headlineBits.push(summary.primary_location);
  if (summary.years_experience_total)
    headlineBits.push(`${summary.years_experience_total} years experience`);

  return (
    <div
      role="status"
      aria-live="polite"
      className="rounded-xl border border-green-700/60 bg-green-950/30 p-4 flex flex-wrap items-start gap-3"
      data-testid="import-success"
    >
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-600 text-white text-sm font-bold">
        ✓
      </span>
      <div className="flex-1 min-w-[240px]">
        <div className="text-sm font-semibold text-green-100">
          {context === 'profile'
            ? `Updated ${summary.name || 'profile'} from CV`
            : `Added ${summary.name || 'candidate'} to the pool`}
        </div>
        {headlineBits.length > 0 && (
          <div className="text-xs text-green-200/90 mt-0.5">{headlineBits.join(' · ')}</div>
        )}
        {evidenceBits.length > 0 && (
          <div className="text-[11px] text-dark-text-secondary mt-1">
            {evidenceBits.join(' · ')}
            {summary.review_status === 'needs_review' && (
              <span className="ml-2 text-amber-400">· needs review</span>
            )}
          </div>
        )}
        {onOpenPerson && (
          <div className="mt-2 flex gap-3 text-xs">
            <button
              type="button"
              onClick={() => onOpenPerson(summary.person_id)}
              className="text-indigo-300 hover:text-indigo-200 font-semibold"
              data-testid="import-open-person"
            >
              Open profile →
            </button>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="text-xs text-gray-400 hover:text-gray-200"
        aria-label="Dismiss success message"
      >
        Dismiss
      </button>
    </div>
  );
};

const ErrorToast: React.FC<{
  error: ImportError;
  onDismiss: () => void;
  onRetry: () => void;
}> = ({ error, onDismiss, onRetry }) => (
  <div
    role="alert"
    aria-live="assertive"
    className="rounded-xl border border-red-700/70 bg-red-950/30 p-4 flex flex-wrap items-start gap-3"
    data-testid="import-error"
    data-error-kind={error.kind}
  >
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-600 text-white text-sm font-bold">
      !
    </span>
    <div className="flex-1 min-w-[240px]">
      <div className="text-sm font-semibold text-red-100">
        {error.kind === 'cancelled' ? 'Import cancelled' : 'Import failed'}
      </div>
      <div className="text-xs text-red-200/90 mt-0.5">{error.message}</div>
      {error.canRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 text-xs px-3 py-1 rounded-lg bg-indigo-700/60 hover:bg-indigo-700 text-white font-semibold"
          data-testid="import-retry-btn"
        >
          Retry
        </button>
      )}
    </div>
    <button
      type="button"
      onClick={onDismiss}
      className="text-xs text-gray-400 hover:text-gray-200"
      aria-label="Dismiss error"
    >
      Dismiss
    </button>
  </div>
);
